import React, { useState } from 'react';
import { Plus, GripVertical, FilePlus2, ArrowRightLeft, Layers, Pencil, Trash2 } from 'lucide-react';
import { DEFAULT_CLIPBOARD_FILL_LABEL, FieldType, FormField, FormSchema, FormStep } from '../types';
import { dangerIconButton, iconButton, primaryButton, secondaryButton } from '../ui';
import { buildLibraryDynamicSchema, builderFieldCount, getLibraryEntries } from './helpers';
import LibraryFieldPicker from './LibraryFieldPicker';
import { inputSmall } from './panelParts';

type FieldListPanelProps = {
  schema: FormSchema;
  step: FormStep;
  stepIndex: number;
  fieldKey: string | undefined;
  allKeys: { key: string; label: string; type: FieldType }[];
  /** Form-level sensor type and category: they identify the dynamic field library. */
  sensorType?: string;
  category?: string;
  dragKey: string | undefined;
  setDragKey: (key: string | undefined) => void;
  movingFieldKey: string | undefined;
  setMovingFieldKey: (key: string | undefined) => void;
  onSelectField: (key: string) => void;
  onAddField: () => void;
  onUpdateStep: (next: FormStep) => void;
  onMoveField: (from: number, to: number) => void;
  onDuplicateField: (source: FormField) => void;
  onDeleteField: (key: string) => void;
  onMoveFieldToStep: (sourceKey: string, targetIndex: number) => void;
};

export default function FieldListPanel({
  schema,
  step,
  stepIndex,
  fieldKey,
  allKeys,
  sensorType,
  category,
  dragKey,
  setDragKey,
  movingFieldKey,
  setMovingFieldKey,
  onSelectField,
  onAddField,
  onUpdateStep,
  onMoveField,
  onDuplicateField,
  onDeleteField,
  onMoveFieldToStep,
}: FieldListPanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const libraryEntries = getLibraryEntries(step?.dynamic_schema);
  const canUseLibrary = !!sensorType && !!category;

  const applyLibrarySelection = (entryKeys: string[]) => {
    setPickerOpen(false);
    if (entryKeys.length === 0) {
      if (libraryEntries) onUpdateStep({ ...step, dynamic_schema: undefined });
      return;
    }
    onUpdateStep({ ...step, dynamic_schema: buildLibraryDynamicSchema(entryKeys, allKeys) });
  };

  return (
        <main className="min-h-0 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600">
                Step {stepIndex + 1} of {schema.steps.length}
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <input
                  className="w-full max-w-[280px] border-0 bg-transparent p-0 text-[15px] font-semibold text-slate-900 outline-none focus:ring-0"
                  value={step?.title || ''}
                  onChange={(e) =>
                    onUpdateStep({ ...step, title: e.target.value })
                  }
                />
                <span className="hidden rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500 sm:inline">
                  {builderFieldCount(step)} fields
                </span>
              </div>
            </div>
            <div data-tour="editor-field-actions" className="flex items-center gap-1.5">
              <button className={primaryButton} onClick={onAddField}>
                <Plus className="h-3.5 w-3.5" /> Add field
              </button>
            </div>
          </div>

          <div className="px-4 py-3">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">
                Step description
              </span>
              <textarea
                className={inputSmall()}
                rows={2}
                value={step?.description || ''}
                onChange={(e) =>
                  onUpdateStep({ ...step, description: e.target.value })
                }
              />
            </label>
          </div>

          <div className="mx-4 mb-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <label className="flex cursor-pointer items-start gap-2 text-[12px] font-medium text-slate-700">
              <input
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                type="checkbox"
                checked={!!step?.clipboard_fill?.enabled}
                onChange={(e) => onUpdateStep({ ...step, clipboard_fill: { ...step.clipboard_fill, enabled: e.target.checked } })}
              />
              <span>
                Show a “fill from copied data” button
                <span className="block text-[10px] font-normal leading-4 text-slate-500">Reads the clipboard and fills this step’s visible fields from top to bottom, one value per line (or tab-separated cells).</span>
              </span>
            </label>
            {step?.clipboard_fill?.enabled && (
              <label className="mt-2 block">
                <span className="text-[11px] font-medium text-slate-600">Button title</span>
                <input
                  className={inputSmall()}
                  maxLength={60}
                  placeholder={DEFAULT_CLIPBOARD_FILL_LABEL}
                  value={step.clipboard_fill.label ?? ''}
                  onChange={(e) => onUpdateStep({ ...step, clipboard_fill: { ...step.clipboard_fill!, label: e.target.value || undefined } })}
                />
              </label>
            )}
          </div>

          <div data-tour="editor-dynamic" className="mx-4 mb-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold text-slate-800">Runtime dynamic fields</div>
                <p className="mt-0.5 max-w-2xl text-[10px] leading-4 text-slate-500">
                  Optional. Select fields from the dynamic field library of this form’s sensor type and category, so you know up front which fields the step will show. They are resolved when the step is entered and become normal form controls after this step’s own fields.
                </p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <button type="button" className={secondaryButton} onClick={() => setPickerOpen(true)} disabled={!canUseLibrary} title={canUseLibrary ? undefined : 'This form has no sensor type and category'}>
                  {libraryEntries ? <><Pencil className="h-3.5 w-3.5" /> Edit selection</> : <><Layers className="h-3.5 w-3.5" /> Select from library</>}
                </button>
              </div>
            </div>
            {libraryEntries && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {libraryEntries.map((key) => (
                  <span key={key} className="rounded-md border border-primary-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-700"><code>{key}</code></span>
                ))}
                <button type="button" className="ml-1 text-[10px] font-medium text-red-600 hover:underline" onClick={() => applyLibrarySelection([])}>Remove</button>
              </div>
            )}
          </div>

          <div data-tour="editor-fields" className="space-y-2 px-4 pb-5">
            {step?.fields.map((f, i) => (
              <div key={`${step.id}-${i}`}>
                <div
                draggable
                onDragStart={() => setDragKey(f.key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragKey) {
                    onMoveField(
                      step.fields.findIndex((x) => x.key === dragKey),
                      i,
                    );
                    setDragKey(undefined);
                  }
                }}
                onDragEnd={() => setDragKey(undefined)}
                onClick={() => onSelectField(f.key)}
                className={`group flex cursor-pointer items-center gap-2.5 rounded-xl border bg-white p-3 shadow-sm transition hover:border-slate-300 hover:shadow-md ${f.key === fieldKey ? 'border-primary-300 ring-2 ring-primary-50' : 'border-slate-200'} ${dragKey === f.key ? 'opacity-40' : ''}`}
              >
                <div
                  className="cursor-grab text-slate-300 transition group-hover:text-slate-500"
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-100 text-[10px] font-semibold text-slate-500">
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <strong className="truncate text-[13px] font-medium text-slate-800">
                      {f.label || 'Untitled field'}
                    </strong>
                    <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500">
                      {f.type.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-[10px] text-slate-400">
                    <span>{f.label || 'Untitled field'}</span><span className="mx-1">·</span><span className="font-mono">{f.key}</span>
                  </div>
                </div>
                <div className="hidden items-center gap-1.5 md:flex">
                  {f.validations.some((r) => r.type === 'required') && (
                    <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                      Required
                    </span>
                  )}
                  {(f.visible_when ||
                    f.required_when ||
                    f.disabled_when ||
                    f.reset_rules.length > 0) && (
                    <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-medium text-violet-700">
                      Logic
                    </span>
                  )}
                  {f.type === 'dynamic_group' && (
                    <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[9px] font-medium text-sky-700">Dynamic API</span>
                  )}
                  {[
                    'select',
                    'multi_select',
                    'radio',
                    'checkbox_group',
                  ].includes(f.type) && (
                    <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[9px] font-medium text-sky-700">
                      {f.data_source?.response.type === 'options' ? 'API' : 'Options'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    className={iconButton}
                    type="button"
                    title="Duplicate field"
                    aria-label="Duplicate field"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateField(f);
                    }}
                  >
                    <FilePlus2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className={`${iconButton} ${movingFieldKey === f.key ? 'bg-primary-50 text-primary-700' : ''}`}
                    type="button"
                    title="Move to another step"
                    aria-label={`Move ${f.label || f.key} to another step`}
                    onClick={(e) => { e.stopPropagation(); setMovingFieldKey(movingFieldKey === f.key ? undefined : f.key); }}
                    disabled={schema.steps.length < 2}
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className={dangerIconButton}
                    type="button"
                    title="Delete field"
                    aria-label="Delete field"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteField(f.key);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                </div>
                {movingFieldKey === f.key && schema.steps.length > 1 && (
                  <div className="ml-10 mt-1 rounded-lg border border-slate-200 bg-white p-2 shadow-sm" onClick={(e) => e.stopPropagation()}>
                    <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">Move to step</div>
                    <div className="grid gap-1 sm:grid-cols-2">
                      {schema.steps.map((target, targetIndex) => targetIndex !== stepIndex && (
                        <button
                          key={target.id}
                          type="button"
                          className="flex items-center justify-between rounded-md px-2 py-1.5 text-left text-[10px] text-slate-600 hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => onMoveFieldToStep(f.key, targetIndex)}
                        >
                          <span className="truncate">{target.title || `Step ${targetIndex + 1}`}</span>
                          <span className="ml-2 shrink-0 text-[9px] text-slate-400">{target.fields.length} fields</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            ))}
            {step?.fields.length === 0 && (
              <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-6 py-14 text-center">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-50 text-primary-600">
                  <Plus className="h-5 w-5" />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-slate-800">
                  Start building this step
                </h3>
                <p className="mt-1 max-w-sm text-[11px] leading-5 text-slate-500">
                  Add fields, then drag them to set their order. Select a field
                  to configure its behaviour.
                </p>
                <button className={`${primaryButton} mt-4`} onClick={onAddField}>
                  <Plus className="h-3.5 w-3.5" /> Add first field
                </button>
              </div>
            )}
          </div>
          {pickerOpen && sensorType && category && (
            <LibraryFieldPicker
              sensorType={sensorType}
              category={category}
              selected={libraryEntries || []}
              hasRangeField={allKeys.some((item) => item.key === 'range')}
              onSave={applyLibrarySelection}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </main>
  );
}
